import React, { Fragment, useEffect, useRef, useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import Button from "@material-ui/core/Button";
import Checkbox from "@material-ui/core/Checkbox";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableContainer from "@material-ui/core/TableContainer";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import { makeStyles } from "@material-ui/core/styles";
import { Loader } from "../components/Loader";
import { dateHelperService } from "../services/date-helper.service";
import { FORM_STATUS } from "../services/form.store";
import {
  formVersionStore,
  versionListState,
} from "../services/form-version.store";
import CompareRevisionsDialog from "./CompareRevisionsDialog";

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    minHeight: "80%",
    minWidth: "70%",
    padding: "6px",
  },
  titleBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  actionBar: {
    padding: "0 24px 12px",
  },
  tableHeading: {
    fontWeight: 600,
    fontSize: "14px",
  },
  currentLabel: {
    fontWeight: 700,
    color: theme.palette.primary.main,
  },
  versionLabel: {
    color: theme.palette.primary.main,
  },
  hint: {
    padding: "0 24px 8px",
    color: theme.palette.text.secondary,
    fontSize: "13px",
  },
  emptyState: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "40vh",
  },
}));

const getVersionNumber = (item) =>
  item.formStatus === FORM_STATUS.PUBLISHED
    ? item.publishedVersion
    : item.version;

export default function VersionHistoryDialog(props) {
  const { open, onClose, formCode, currentForm } = props;
  const classes = useStyles();
  const [state, setState] = useState({ loading: true, versions: [] });
  const [selectedIds, setSelectedIds] = useState([]);
  const [showCompare, setShowCompare] = useState(false);
  const preselectDone = useRef(false);

  useEffect(() => {
    const subscription = versionListState.subscribe((res) => {
      setState(res || { loading: false, versions: [] });
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (open && formCode) {
      preselectDone.current = false;
      setSelectedIds([]);
      formVersionStore.loadVersionHistory(formCode);
    }
  }, [open, formCode]);

  const { loading, versions, error } = state;

  // Only published revisions are comparable; drafts are excluded from the picker.
  const fetchedVersions = (versions || []).filter(
    (v) => v.formStatus === FORM_STATUS.PUBLISHED,
  );
  const isCurrentPublished =
    currentForm?.formStatus === FORM_STATUS.PUBLISHED;
  // previousVersions returns only older revisions, so prepend the published form
  // open in the editor as CURRENT when it is missing from the list.
  const displayVersions =
    isCurrentPublished &&
    currentForm.id &&
    !fetchedVersions.some((v) => v.id === currentForm.id)
      ? [currentForm, ...fetchedVersions]
      : fetchedVersions;
  const currentId = isCurrentPublished
    ? currentForm.id
    : displayVersions[0]?.id;

  // Preselect the two most recent versions once the list is loaded.
  useEffect(() => {
    if (!preselectDone.current && displayVersions.length >= 2) {
      setSelectedIds([displayVersions[0].id, displayVersions[1].id]);
      preselectDone.current = true;
    }
  }, [displayVersions]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((x) => x !== id);
      }
      if (prev.length >= 2) {
        return [prev[1], id];
      }
      return [...prev, id];
    });
  };

  const handleCompare = () => {
    const picked = displayVersions.filter((v) => selectedIds.includes(v.id));
    if (picked.length !== 2) {
      return;
    }
    // Lower index means newer, so treat it as the "current" side of the diff.
    const ordered = [...picked].sort(
      (a, b) => displayVersions.indexOf(a) - displayVersions.indexOf(b),
    );
    const [current, previous] = ordered;
    formVersionStore.compareRevisions(current, previous);
    setShowCompare(true);
  };

  return (
    <Fragment>
      <Dialog
        open={open}
        onClose={onClose}
        aria-labelledby="version-history-dialog-title"
        classes={{ paper: classes.dialogPaper }}
        fullWidth
        maxWidth="md"
      >
        <div className={classes.titleBar}>
          <DialogTitle id="version-history-dialog-title">
            Version History
          </DialogTitle>
          <IconButton aria-label="close" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </div>

        {loading ? (
          <Loader position="center" loading={loading} />
        ) : error ? (
          <div className={classes.emptyState}>
            <Typography variant="h6">{error}</Typography>
          </div>
        ) : displayVersions && displayVersions.length > 0 ? (
          <Fragment>
            <div className={classes.actionBar}>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleCompare}
                disabled={selectedIds.length !== 2}
              >
                Compare selected versions
              </Button>
            </div>
            <div className={classes.hint}>
              Select two versions to compare.
            </div>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell className={classes.tableHeading}>
                      Version
                    </TableCell>
                    <TableCell className={classes.tableHeading}>
                      Published
                    </TableCell>
                    <TableCell className={classes.tableHeading}>
                      Changed By
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayVersions.map((item) => (
                    <TableRow hover key={item.id}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          color="primary"
                          checked={selectedIds.includes(item.id)}
                          onChange={() => toggleSelect(item.id)}
                        />
                      </TableCell>
                      <TableCell>
                        {item.id === currentId ? (
                          <span className={classes.currentLabel}>
                            CURRENT (v. {getVersionNumber(item)})
                          </span>
                        ) : (
                          <span className={classes.versionLabel}>
                            v. {getVersionNumber(item)}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {dateHelperService.formattedDateFromUTS(
                          item.lastUpdatedDate,
                        )}
                      </TableCell>
                      <TableCell>{item.lastUpdatedBy}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Fragment>
        ) : (
          <div className={classes.emptyState}>
            <Typography variant="h6">No version history found.</Typography>
          </div>
        )}
      </Dialog>

      {showCompare && (
        <CompareRevisionsDialog
          open={showCompare}
          onClose={() => setShowCompare(false)}
        />
      )}
    </Fragment>
  );
}
